import { create } from 'zustand';
import {
  getDashboardStats,
  getTopCourses,
  getTasks,
  getLearningHoursData,
  getLearningProgress,
  performTaskAction,
  getAdminOrganizations,
  getOrganizationStats,
  DashboardStat,
  CourseProgress,
  Task,
  LearningHoursData,
  LearningProgress,
  DateRange,
  TaskActionType,
  Organization,
  OrganizationStats,
} from '@/services/PagesServices/AdminDashboardService';

interface AdminDashboardState {
  // Data states
  stats: DashboardStat[] | null;
  courses: CourseProgress[] | null;
  tasks: Task[] | null;
  learningHoursData: LearningHoursData[] | null;
  learningProgress: LearningProgress | null;

  // Organization states
  organizations: Organization[] | null;
  selectedOrganization: Organization | null;
  organizationStats: OrganizationStats | null;

  // Date state - single date for stats
  selectedDate: Date;
  // Date range state - for other data (courses, tasks, etc.)
  dateRange: DateRange;

  // Granular loading states
  loadingStats: boolean;
  loadingCourses: boolean;
  loadingTasks: boolean;
  loadingLearningHours: boolean;
  loadingProgress: boolean;
  loadingOrganizations: boolean;
  loadingOrganizationStats: boolean;

  // Task action loading states (per task)
  taskActionLoading: Record<string, boolean>;

  // Granular error states
  errorStats: string | null;
  errorCourses: string | null;
  errorTasks: string | null;
  errorLearningHours: string | null;
  errorProgress: string | null;
  errorOrganizations: string | null;
  errorOrganizationStats: string | null;

  // Actions
  fetchStats: () => Promise<void>;
  fetchCourses: () => Promise<void>;
  fetchTasks: () => Promise<void>;
  fetchLearningHoursData: () => Promise<void>;
  fetchLearningProgress: () => Promise<void>;
  fetchAllData: () => Promise<void>;
  setSelectedDate: (date: Date) => void;
  setDateRange: (startDate: Date, endDate: Date) => void;
  handleTaskAction: (taskId: string, action: TaskActionType) => Promise<{ success: boolean; message?: string }>;
  removeTask: (taskId: string) => void;

  // Organization actions
  fetchOrganizations: () => Promise<void>;
  fetchOrganizationStats: (organizationId: number) => Promise<void>;
  setSelectedOrganization: (organization: Organization) => void;
}

// Default date range: last 7 days
const getDefaultDateRange = (): DateRange => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);
  return { startDate, endDate };
};

const useAdminDashboardStore = create<AdminDashboardState>((set, get) => ({
  // Initial data states
  stats: null,
  courses: null,
  tasks: null,
  learningHoursData: null,
  learningProgress: null,

  // Initial organization states
  organizations: null,
  selectedOrganization: null,
  organizationStats: null,

  // Initial date state
  selectedDate: new Date(),
  // Initial date range
  dateRange: getDefaultDateRange(),

  // Initial loading states
  loadingStats: false,
  loadingCourses: false,
  loadingTasks: false,
  loadingLearningHours: false,
  loadingProgress: false,
  loadingOrganizations: false,
  loadingOrganizationStats: false,

  // Task action loading
  taskActionLoading: {},

  // Initial error states
  errorStats: null,
  errorCourses: null,
  errorTasks: null,
  errorLearningHours: null,
  errorProgress: null,
  errorOrganizations: null,
  errorOrganizationStats: null,

  fetchStats: async () => {
    console.log('🏪 AdminDashboardStore: fetchStats called (real-time data)');
    set({ loadingStats: true, errorStats: null });

    try {
      // Always fetch real-time stats (date parameter is ignored by backend now)
      const response = await getDashboardStats(new Date());

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load stats');
      }

      set({
        stats: response.data ?? [],
        loadingStats: false,
      });
      console.log('🏪 AdminDashboardStore: Stats loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchStats:', err);
      set({
        errorStats: err.message ?? 'Something went wrong',
        loadingStats: false,
      });
    }
  },

  fetchCourses: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchCourses called');
    set({ loadingCourses: true, errorCourses: null });

    try {
      const response = await getTopCourses(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load courses');
      }

      set({
        courses: response.data ?? [],
        loadingCourses: false,
      });
      console.log('🏪 AdminDashboardStore: Courses loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchCourses:', err);
      set({
        errorCourses: err.message ?? 'Something went wrong',
        loadingCourses: false,
      });
    }
  },

  fetchTasks: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchTasks called');
    set({ loadingTasks: true, errorTasks: null });

    try {
      const response = await getTasks(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load tasks');
      }

      set({
        tasks: response.data ?? [],
        loadingTasks: false,
      });
      console.log('🏪 AdminDashboardStore: Tasks loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchTasks:', err);
      set({
        errorTasks: err.message ?? 'Something went wrong',
        loadingTasks: false,
      });
    }
  },

  fetchLearningHoursData: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchLearningHoursData called');
    set({ loadingLearningHours: true, errorLearningHours: null });

    try {
      const response = await getLearningHoursData(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load learning hours');
      }

      set({
        learningHoursData: response.data ?? [],
        loadingLearningHours: false,
      });
      console.log('🏪 AdminDashboardStore: Learning hours loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchLearningHoursData:', err);
      set({
        errorLearningHours: err.message ?? 'Something went wrong',
        loadingLearningHours: false,
      });
    }
  },

  fetchLearningProgress: async () => {
    const { dateRange } = get();
    console.log('🏪 AdminDashboardStore: fetchLearningProgress called');
    set({ loadingProgress: true, errorProgress: null });

    try {
      const response = await getLearningProgress(dateRange);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load learning progress');
      }

      set({
        learningProgress: response.data ?? null,
        loadingProgress: false,
      });
      console.log('🏪 AdminDashboardStore: Learning progress loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchLearningProgress:', err);
      set({
        errorProgress: err.message ?? 'Something went wrong',
        loadingProgress: false,
      });
    }
  },

  fetchAllData: async () => {
    console.log('🏪 AdminDashboardStore: fetchAllData called');
    const {
      fetchStats,
      fetchCourses,
      fetchTasks,
      fetchLearningHoursData,
      fetchLearningProgress,
    } = get();

    // Fetch all data in parallel
    await Promise.all([
      fetchStats(),
      fetchCourses(),
      fetchTasks(),
      fetchLearningHoursData(),
      fetchLearningProgress(),
    ]);
  },

  setSelectedDate: (date: Date) => {
    console.log('🏪 AdminDashboardStore: setSelectedDate called', date);

    // Update selected date
    set({ selectedDate: date });

    // Clear stats data to trigger refetch
    set({ stats: null });

    // Refetch stats with new date
    get().fetchStats();
  },

  setDateRange: (startDate: Date, endDate: Date) => {
    console.log('🏪 AdminDashboardStore: setDateRange called', { startDate, endDate });

    // Update date range
    set({ dateRange: { startDate, endDate } });

    // Clear non-stats data to trigger refetch
    set({
      courses: null,
      tasks: null,
      learningHoursData: null,
      learningProgress: null,
    });

    // Refetch data with new date range (excluding stats)
    const { fetchCourses, fetchTasks, fetchLearningHoursData, fetchLearningProgress } = get();
    Promise.all([
      fetchCourses(),
      fetchTasks(),
      fetchLearningHoursData(),
      fetchLearningProgress(),
    ]);
  },

  handleTaskAction: async (taskId: string, action: TaskActionType) => {
    console.log(`🏪 AdminDashboardStore: handleTaskAction called - ${action} on ${taskId}`);

    // Set loading state for this specific task
    set((state) => ({
      taskActionLoading: { ...state.taskActionLoading, [taskId]: true },
    }));

    try {
      const response = await performTaskAction(taskId, action);

      // Clear loading state
      set((state) => ({
        taskActionLoading: { ...state.taskActionLoading, [taskId]: false },
      }));

      if (!response.success) {
        return {
          success: false,
          message: response.error?.message ?? `Failed to ${action} task`,
        };
      }

      // If action was successful and it's approve or reject, remove task from list
      if (action === 'approve' || action === 'reject') {
        get().removeTask(taskId);
      }

      return {
        success: true,
        message: response.data?.message ?? `Task ${action}ed successfully`,
      };
    } catch (err: any) {
      console.error(`🏪 AdminDashboardStore: Error in handleTaskAction:`, err);

      // Clear loading state
      set((state) => ({
        taskActionLoading: { ...state.taskActionLoading, [taskId]: false },
      }));

      return {
        success: false,
        message: err.message ?? 'Something went wrong',
      };
    }
  },

  removeTask: (taskId: string) => {
    set((state) => ({
      tasks: state.tasks?.filter((task) => task.id !== taskId) ?? null,
    }));
  },

  fetchOrganizations: async () => {
    console.log('🏪 AdminDashboardStore: fetchOrganizations called');
    set({ loadingOrganizations: true, errorOrganizations: null });

    try {
      console.log('🌐 Calling getAdminOrganizations API...');
      const response = await getAdminOrganizations();

      console.log('📡 API Response:', response);

      if (!response.success) {
        console.error('❌ API returned error:', response.error);
        throw new Error(response.error?.message ?? 'Failed to load organizations');
      }

      const organizations = response.data ?? [];
      console.log('📊 Organizations received:', organizations.length);
      console.log('🗂️ Organizations data:', organizations);

      set({
        organizations,
        loadingOrganizations: false,
      });

      // Auto-select first organization if available and none is selected
      if (organizations.length > 0 && !get().selectedOrganization) {
        const firstOrg = organizations[0];
        console.log('✅ Auto-selecting first organization:', firstOrg.name);
        set({ selectedOrganization: firstOrg });
        // Fetch stats for the first organization
        get().fetchOrganizationStats(firstOrg.id);
      } else if (organizations.length === 0) {
        console.warn('⚠️ No organizations found for this admin user!');
      }

      console.log('🏪 AdminDashboardStore: Organizations loaded successfully');
    } catch (err: any) {
      console.error('❌ AdminDashboardStore: Error in fetchOrganizations:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      set({
        errorOrganizations: err.message ?? 'Something went wrong',
        loadingOrganizations: false,
      });
    }
  },

  fetchOrganizationStats: async (organizationId: number) => {
    console.log('🏪 AdminDashboardStore: fetchOrganizationStats called for org:', organizationId);
    set({ loadingOrganizationStats: true, errorOrganizationStats: null });

    try {
      const response = await getOrganizationStats(organizationId);

      if (!response.success) {
        throw new Error(response.error?.message ?? 'Failed to load organization stats');
      }

      set({
        organizationStats: response.data ?? null,
        loadingOrganizationStats: false,
      });
      console.log('🏪 AdminDashboardStore: Organization stats loaded successfully');
    } catch (err: any) {
      console.error('🏪 AdminDashboardStore: Error in fetchOrganizationStats:', err);
      set({
        errorOrganizationStats: err.message ?? 'Something went wrong',
        loadingOrganizationStats: false,
      });
    }
  },

  setSelectedOrganization: (organization: Organization) => {
    console.log('🏪 AdminDashboardStore: setSelectedOrganization called', organization);
    set({ selectedOrganization: organization });

    // Fetch stats for the newly selected organization
    get().fetchOrganizationStats(organization.id);
  },
}));

export default useAdminDashboardStore;
